import {
  Autocomplete,
  Box,
  CircularProgress,
  Grid,
  TextField,
  Typography,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
// import {tenantServiceCreate, tenantServiceDetail, tenantServiceUpdate} from 'src/apis';
import {
  tenantServiceCreate,
  tenantServiceDetail,
  tenantServiceUpdate,
  tenantServiceUpdateCallMethod,
} from "src/apis/autoGenerate/services";
// import {Room, Tenant, User} from 'src/apis/autoGenerate';
import { Room, Tenant, User } from "src/apis/autoGenerate/types";
import Modal from "src/components/Modal";
import {
  AutocompleteOption,
  AutocompleteVal,
} from "src/components/SearchApartment";
import message from "src/components/Message";
import {
  useAccessGroups,
  useApartmentBuildingUnit,
  useOrgId,
  useLicenseQuota,
} from "src/hooks";
import InfoIcon from "@mui/icons-material/Info";

interface AddUserModalProps {
  open: boolean;
  handleConfirm: VoidFunction;
  handleCancel: VoidFunction;
  initData?: Tenant;
  tenantDetailId?: string;
  /** Force read-only detail view (e.g. PDK auto-sync view). */
  readOnly?: boolean;
  /**
   * When PDK auto-sync is on: allow changing apartment / building / unit only.
   * Identity fields stay locked to PDK.
   */
  unitOnly?: boolean;
}

function getInitFormData() {
  return {
    email: "",
    leaseStart: "",
    leaseEnd: "",
    roomId: "",
    accessGroupIds: [] as string[],
    mobile: "",
    countryCode: "+1",
    phoneExt: "",
    callMethod: "MOBILE_APP" as "MOBILE_APP" | "SIP_PHONE",
  };
}

// Phone number validation
function validatePhoneNumber(phone: string): boolean {
  if (!phone) return true; // Optional field
  const cleaned = phone.replace(/\D/g, "");
  // Validates 10-digit phone numbers (US format)
  return cleaned.length === 10;
}

// Phone extension validation
function validatePhoneExtension(ext: string): boolean {
  if (!ext || ext.trim() === "") return true; // Optional field
  // Must contain only digits (0-9) and max 20 characters
  return /^\d{1,20}$/.test(ext.trim());
}

// Format phone number as user types
function formatPhoneNumber(value: string): string {
  const cleaned = value.replace(/\D/g, "");
  if (cleaned.length <= 3) {
    return cleaned;
  } else if (cleaned.length <= 6) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
  } else {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  }
}

// Parse phone number from full format (e.g., "+11234567890" -> "1234567890")
function parsePhoneNumber(phone: string): string {
  if (!phone) return "";
  // Remove all non-digit characters first
  const digitsOnly = phone.replace(/\D/g, "");
  // If it starts with 1 and has 11 digits, remove the leading 1 (US country code)
  if (digitsOnly.length === 11 && digitsOnly.startsWith("1")) {
    return digitsOnly.slice(1);
  }
  // If it has 10 digits, return as is
  if (digitsOnly.length === 10) {
    return digitsOnly;
  }
  // Otherwise, try to remove country code pattern (+1, +11, etc.)
  // Remove leading 1 if present (US country code)
  if (digitsOnly.length > 10 && digitsOnly.startsWith("1")) {
    return digitsOnly.slice(1);
  }
  return digitsOnly;
}

// Remove country code from phone number string for display
function removeCountryCodeFromPhone(phone: string): string {
  if (!phone) return "";
  // Remove +1, +1 with spaces, or just 1 at the start
  return phone
    .replace(/^\+\s*1\s*/i, "")
    .replace(/^\+1\s*/, "")
    .replace(/^1\s*/, "")
    .trim();
}

// Extract country code from phone string
function extractCountryCode(phone: string): string {
  if (!phone) return "+1";
  // For US/Canada numbers, always use +1
  // Check if it starts with +1 (must check this BEFORE checking for longer codes)
  // This handles cases like +11234567890 where we want +1, not +112
  if (phone.startsWith("+1")) {
    // Make sure it's not a longer country code by checking the next character
    // If the next character after +1 is a digit, it's part of the phone number, so country code is +1
    // If it's not a digit or the string is just +1, country code is +1
    if (
      phone.length === 2 ||
      (phone.length > 2 && /^\d/.test(phone.slice(2)))
    ) {
      return "+1";
    }
  }
  // For other countries, match country code (1-3 digits after +)
  const match = phone.match(/^(\+\d{1,3})/);
  return match ? match[1] : "+1";
}

export default function AddUserModal({
  open,
  initData,
  tenantDetailId,
  readOnly = false,
  unitOnly = false,
  handleConfirm,
  handleCancel,
}: AddUserModalProps) {
  const {
    apartmentOptions,
    setSelectedApartment,
    loadingBuildings,
    buildingOptions,
    setSelectedBuilding,
    loadingUnits,
    setSelectedUnit,
    selectedApartmentValue,
    selectBuildingValue,
    selectedUnitValue,
    buildingSelectDisabled,
    unitSelectDisabled,
    unitOptions,
    handleSelectApartment,
    handleSelectBuilding,
  } = useApartmentBuildingUnit();
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState(getInitFormData());
  const [user, setUser] = useState<User>({
    firstName: "",
    lastName: "",
    email: "",
  });
  const [room, setRoom] = useState<Room>();
  const { getAccessGroups, accessGroupsOptions } = useAccessGroups();
  const [accessGroupIdsValue, setAccessGroupIdsValue] = useState<
    AutocompleteOption[]
  >([]);
  const [phoneError, setPhoneError] = useState(false);
  const [phoneExtError, setPhoneExtError] = useState(false);

  const { orgId } = useOrgId();

  // SIP license quota gates the call method control
  const { hasSipLicense, hasSipQuotaAvailable, sipAvailableUnits } =
    useLicenseQuota();

  // A resident already on SIP keeps their seat, so they can always stay on it
  const isAlreadySip = initData?.user?.callMethod === "SIP_PHONE";
  const canSelectSip = hasSipQuotaAvailable || isAlreadySip;

  useEffect(() => {
    // Pass the orgId to the getAccessGroups function
    if (orgId) {
      getAccessGroups();
    }
  }, [getAccessGroups, orgId]);

  const setData = useCallback(
    (data: Tenant | undefined) => {
      if (data) {
        const phone = data.mobile || "";
        const countryCode = extractCountryCode(phone);
        let phoneNumberDigits = parsePhoneNumber(phone);

        // Ensure we have exactly 10 digits (remove any extra digits)
        if (phoneNumberDigits.length > 10) {
          phoneNumberDigits = phoneNumberDigits.slice(-10); // Take last 10 digits
        }

        // Format the phone number for display (ensure it's only 10 digits)
        let phoneNumberFormatted =
          phoneNumberDigits && phoneNumberDigits.length === 10
            ? formatPhoneNumber(phoneNumberDigits)
            : "";

        // Ensure no country code is in the formatted phone number
        phoneNumberFormatted = removeCountryCodeFromPhone(phoneNumberFormatted);

        setFormData({
          email: data.email as string,
          leaseStart: data.leaseStart as string,
          leaseEnd: data.leaseEnd as string,
          roomId: data.roomId as string,
          accessGroupIds: (data.accessGroups || []).map((_) => _.id as string),
          mobile: phoneNumberFormatted,
          countryCode: countryCode,
          phoneExt: data.phoneExt || data.user?.phoneExt || "",
          callMethod:
            (data.user?.callMethod as "MOBILE_APP" | "SIP_PHONE") ||
            "MOBILE_APP",
        });

        if (data.apartment) {
          setSelectedApartment({
            label: data.apartment.name,
            value: data.apartment.id,
          });
        }
        if (data.building) {
          setSelectedBuilding({
            label: data.building.name,
            value: data.building.id,
          });
        }
        if (data.room) {
          setSelectedUnit({ label: data.room.name, value: data.room.id });
        }
        if (data.accessGroups) {
          setAccessGroupIdsValue(
            data.accessGroups.map((_) => ({ label: _.name, value: _.id })),
          );
        }
        setUser({
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
        });
        setPhoneError(false);
        setPhoneExtError(false);
      } else {
        // Reset for new tenant
        setFormData(getInitFormData());
        setPhoneError(false);
        setPhoneExtError(false);
      }
    },
    [
      setFormData,
      setSelectedApartment,
      setSelectedBuilding,
      setSelectedUnit,
      setAccessGroupIdsValue,
      setUser,
    ],
  );
  useEffect(() => {
    if (tenantDetailId) {
      tenantServiceDetail(tenantDetailId).then((_) => {
        const res = _ as unknown as Tenant;
        setData(res);
        setRoom(res.room);
      });
    }
  }, [setData, tenantDetailId]);
  useEffect(() => {
    setData(initData);
    if (initData?.room) {
      setRoom(initData.room);
    }
  }, [initData, setData]);

  const handleSelectUnit = (e: any, val: AutocompleteVal) => {
    setSelectedUnit(val);
    if (val) {
      setFormData({ ...formData, roomId: val.value });
    }
  };

  const handleSelectAccessGroup = (e: any, val: AutocompleteOption[]) => {
    setAccessGroupIdsValue(val);
    if (val) {
      setFormData({ ...formData, accessGroupIds: val.map((_) => _?.value) });
    }
  };

  const checkParams = () => {
    if (!formData.email) {
      message.warning("Email is required");
      return false;
    }
    if (!formData.roomId) {
      message.warning("Unit is required");
      return false;
    }
    if (!formData.leaseStart) {
      message.warning("Lease Start is required");
      return false;
    }
    if (!formData.leaseEnd) {
      message.warning("Lease End is required");
      return false;
    }
    // Validate phone number if provided
    if (formData.mobile && !validatePhoneNumber(formData.mobile)) {
      setPhoneError(true);
      message.warning("Please enter a valid 10-digit phone number");
      return false;
    }
    // Validate phone extension if provided
    if (formData.phoneExt && !validatePhoneExtension(formData.phoneExt)) {
      setPhoneExtError(true);
      message.warning(
        "Invalid phone extension format. Extension must contain only digits (0-9).",
      );
      return false;
    }
    setPhoneError(false);
    setPhoneExtError(false);
    return true;
  };

  const handleAddUser = () => {
    if (!checkParams()) {
      return;
    }
    setLoading(true);

    // Format phone number with country code
    // Check if phone actually has digits (not just dashes/whitespace)
    // Trim and remove all non-digits to get actual phone digits
    const phoneDigits = formData.mobile
      ? formData.mobile.trim().replace(/\D/g, "")
      : "";
    // Only create full phone if we have at least 10 digits
    const fullPhone =
      phoneDigits && phoneDigits.length >= 10
        ? `${formData.countryCode}${phoneDigits}`
        : undefined; // Use undefined to explicitly clear the phone number

    // Format phone extension: trim and use null if empty
    const phoneExt =
      formData.phoneExt && formData.phoneExt.trim()
        ? formData.phoneExt.trim()
        : null;

    tenantServiceCreate({
      ...formData,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      mobile: fullPhone,
      phoneExt: phoneExt,
    })
      .then((res) => {
        message.success("Add Success");
        handleConfirm();
      })
      .catch((err) => {
        message.error(err.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleEditUser = () => {
    if (!checkParams()) {
      return;
    }
    setLoading(true);

    const tenantId = initData?.id as string;

    if (unitOnly) {
      tenantServiceUpdate(tenantId, {
        id: tenantId,
        tenant: {
          roomId: formData.roomId,
          apartment: selectedApartmentValue
            ? {id: selectedApartmentValue.value}
            : undefined,
          building: selectBuildingValue
            ? {id: selectBuildingValue.value}
            : undefined,
        },
      })
        .then(() => {
          message.success('Unit updated');
          handleConfirm();
        })
        .catch((err: any) => {
          message.error(
            err?.response?.data?.error ||
              err?.response?.data?.message ||
              err?.message ||
              'Failed to update unit'
          );
        })
        .finally(() => {
          setLoading(false);
        });
      return;
    }

    // Format phone number with country code
    // Check if phone actually has digits (not just dashes/whitespace)
    // Trim and remove all non-digits to get actual phone digits
    const phoneDigits = formData.mobile
      ? formData.mobile.trim().replace(/\D/g, "")
      : "";
    // Only create full phone if we have at least 10 digits
    const fullPhone =
      phoneDigits && phoneDigits.length >= 10
        ? `${formData.countryCode}${phoneDigits}`
        : ""; // Use empty string to explicitly clear the phone number

    // Format phone extension: trim and use null if empty
    const phoneExt =
      formData.phoneExt && formData.phoneExt.trim()
        ? formData.phoneExt.trim()
        : null;

    tenantServiceUpdate(tenantId, {
      id: tenantId,
      tenant: {
        ...formData,
        firstName: user.firstName,
        lastName: user.lastName,
        mobile: fullPhone,
        phoneExt: phoneExt,
        apartment: selectedApartmentValue
          ? { id: selectedApartmentValue.value }
          : undefined,
        building: selectBuildingValue
          ? { id: selectBuildingValue.value }
          : undefined,
      },
    })
      .then(async () => {
        // Update call method only if it changed compared to original user value
        const originalCallMethod =
          (initData?.user?.callMethod as "MOBILE_APP" | "SIP_PHONE") ||
          "MOBILE_APP";
        const newCallMethod = (formData.callMethod || originalCallMethod) as
          | "MOBILE_APP"
          | "SIP_PHONE";

        if (tenantId && newCallMethod !== originalCallMethod) {
          try {
            await tenantServiceUpdateCallMethod(tenantId, {
              callMethod: newCallMethod,
            });
          } catch (err: any) {
            message.error(
              err?.response?.data?.error ||
                err?.response?.data?.message ||
                err?.message ||
                "Failed to update call method",
            );
          }
        }

        message.success("Update Success");
        handleConfirm();
      })
      .catch((err) => {
        message.error(err.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const isEdit = useMemo(() => {
    return !!initData;
  }, [initData]);

  const onlyView = useMemo(() => {
    return readOnly || !!tenantDetailId;
  }, [readOnly, tenantDetailId]);

  /** Identity / contact fields locked (view mode or PDK unit-only edit). */
  const identityLocked = onlyView || unitOnly;

  useEffect(() => {}, [buildingOptions, buildingSelectDisabled]);

  useEffect(() => {}, [selectedApartmentValue]);

  return (
    <Modal
      open={open}
      title={
        unitOnly
          ? 'Edit Unit'
          : onlyView
            ? 'Resident Detail'
            : isEdit
              ? 'Edit Resident'
              : 'Add Resident'
      }
      handleCancel={handleCancel}
      handleConfirm={
        onlyView ? handleConfirm : isEdit ? handleEditUser : handleAddUser
      }
      loading={loading}
      cancelText={onlyView ? "Close" : "Cancel"}
      hideConfirm={onlyView}
      modalContent={
        <Box component="form">
          {unitOnly && (
            <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
              Name and email stay managed in pdk.io. You can change where this
              resident is placed in Entrio.
            </Typography>
          )}
          {/* Name Fields */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={6}>
              <Typography
                variant="h6"
                sx={{ mb: 1, fontWeight: 400, fontSize: "1.1rem" }}
              >
                First Name
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="First Name"
                variant="outlined"
                disabled={identityLocked}
                value={user.firstName}
                onChange={(e: any) => {
                  setUser({ ...user, firstName: e.target.value });
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography
                variant="h6"
                sx={{ mb: 1, fontWeight: 400, fontSize: "1.1rem" }}
              >
                Last Name
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Last Name"
                variant="outlined"
                disabled={identityLocked}
                value={user.lastName}
                onChange={(e: any) => {
                  setUser({ ...user, lastName: e.target.value });
                }}
              />
            </Grid>
          </Grid>

          {/* Email */}
          <Typography
            variant="h6"
            sx={{ mb: 1, fontWeight: 400, fontSize: "1.1rem" }}
          >
            Email <span style={{ color: "inherit" }}>*</span>
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Email"
            variant="outlined"
            disabled={isEdit || identityLocked}
            value={formData.email}
            onChange={(e: any) => {
              const email = e.target.value;
              setFormData({ ...formData, email });
              setUser({ ...user, email });
            }}
            sx={{ mb: 3 }}
          />

          {/* Mobile Phone */}
          <Typography
            variant="h6"
            sx={{ mb: 1, fontWeight: 400, fontSize: "1.1rem" }}
          >
            Mobile Phone
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="country-code-label">Code</InputLabel>
              <Select
                labelId="country-code-label"
                label="Code"
                value={formData.countryCode}
                disabled
                sx={{
                  "& .MuiSelect-select": {
                    backgroundColor: "rgba(0, 0, 0, 0.02)",
                  },
                }}
              >
                <MenuItem value="+1">+1 (US/CA)</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              size="small"
              placeholder="123-456-7890"
              variant="outlined"
              disabled={identityLocked}
              value={removeCountryCodeFromPhone(formData.mobile)} // Ensure no country code in value
              error={phoneError}
              helperText={
                phoneError ? "Please enter a valid 10-digit phone number" : ""
              }
              onChange={(e: any) => {
                const input = e.target.value;
                // If input is empty, clear the phone field
                if (!input || input.trim() === "") {
                  setFormData({ ...formData, mobile: "" });
                  setPhoneError(false);
                  return;
                }
                // Remove country code if user accidentally types it (e.g., +1, +11, etc.)
                let cleaned = removeCountryCodeFromPhone(input);
                // Only allow digits and dashes, limit to 12 characters (for formatted: 123-456-7890)
                cleaned = cleaned.replace(/[^\d-]/g, "").slice(0, 12);
                const formatted = formatPhoneNumber(cleaned);
                setFormData({ ...formData, mobile: formatted });
                setPhoneError(false);
              }}
              onBlur={() => {
                if (formData.mobile && !validatePhoneNumber(formData.mobile)) {
                  setPhoneError(true);
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {formData.countryCode}
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Phone Extension */}
          <Typography
            variant="h6"
            sx={{ mb: 1, fontWeight: 400, fontSize: "1.1rem" }}
          >
            Extension (Optional)
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="1234"
            variant="outlined"
            disabled={identityLocked}
            value={formData.phoneExt || ""}
            error={phoneExtError}
            helperText={
              phoneExtError
                ? "Invalid phone extension format. Extension must contain only digits (0-9)."
                : "Enter phone extension for SIP calls (digits only, max 20 characters)"
            }
            onChange={(e: any) => {
              const input = e.target.value;
              // Only allow digits, limit to 20 characters
              const cleaned = input.replace(/\D/g, "").slice(0, 20);
              setFormData({ ...formData, phoneExt: cleaned });
              setPhoneExtError(false);
            }}
            onBlur={() => {
              if (
                formData.phoneExt &&
                !validatePhoneExtension(formData.phoneExt)
              ) {
                setPhoneExtError(true);
              }
            }}
            sx={{ mb: 3 }}
          />

          {/* Call Method (edit when unlocked; read-only display in view mode) */}
          {(isEdit || onlyView) && hasSipLicense && (
            <>
              <Box
                sx={{ display: "flex", alignItems: "center", mb: 1, gap: 0.5 }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 400, fontSize: "1.1rem" }}
                >
                  Call Method
                </Typography>
                {!identityLocked && (
                  <Tooltip
                    title={
                      <Box sx={{ maxWidth: 260 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, mb: 0.25 }}
                        >
                          Mobile App (Default)
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          Incoming calls ring your mobile app.
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, mb: 0.25 }}
                        >
                          SIP Phone
                        </Typography>
                        <Typography variant="body2">
                          Incoming calls ring your phone number instead of the
                          mobile app.
                        </Typography>
                      </Box>
                    }
                    placement="right"
                    arrow
                  >
                    <InfoIcon
                      sx={{
                        fontSize: "1rem",
                        color: "#9CA3AF",
                        cursor: "default",
                      }}
                    />
                  </Tooltip>
                )}
              </Box>
              <FormControl size="small" fullWidth sx={{ mb: 3 }}>
                <InputLabel id="call-method-label">Call Method</InputLabel>
                <Select
                  labelId="call-method-label"
                  label="Call Method"
                  value={formData.callMethod || "MOBILE_APP"}
                  disabled={identityLocked}
                  onChange={(e: any) => {
                    const value = e.target.value as "MOBILE_APP" | "SIP_PHONE";
                    setFormData({ ...formData, callMethod: value });
                  }}
                >
                  <MenuItem value="MOBILE_APP">Mobile App</MenuItem>
                  <MenuItem value="SIP_PHONE" disabled={!canSelectSip}>
                    SIP Phone
                  </MenuItem>
                </Select>
                {!identityLocked && !canSelectSip && (
                  <FormHelperText>
                    All SIP licenses are in use ({sipAvailableUnits} available).
                    Add SIP licenses on the License page or switch another
                    resident to Mobile App.
                  </FormHelperText>
                )}
              </FormControl>
            </>
          )}

          {/* Pin Code (only for view mode) */}
          {onlyView && room && (
            <>
              <Typography
                variant="h6"
                sx={{ mb: 1, fontWeight: 400, fontSize: "1.1rem" }}
              >
                Pin Code
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Pin Code"
                variant="outlined"
                disabled={true}
                value={room?.pinCode}
                sx={{ mb: 3 }}
              />
            </>
          )}

          {/* Search Property */}
          <Typography
            variant="h6"
            sx={{ mb: 1, fontWeight: 400, fontSize: "1.1rem" }}
          >
            Search property
          </Typography>
          <Autocomplete
            disabled={onlyView}
            options={apartmentOptions}
            fullWidth
            value={selectedApartmentValue}
            onChange={handleSelectApartment}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search property"
                variant="outlined"
                size="small"
              />
            )}
            sx={{ mb: 3 }}
          />

          {/* Select Building */}
          <Typography
            variant="h6"
            sx={{ mb: 1, fontWeight: 400, fontSize: "1.1rem" }}
          >
            Select Building
          </Typography>
          <Autocomplete
            disabled={buildingSelectDisabled || onlyView}
            fullWidth
            options={buildingOptions}
            value={selectBuildingValue}
            onChange={handleSelectBuilding}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select Building"
                variant="outlined"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingBuildings ? (
                        <CircularProgress color="inherit" size={16} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{ mb: 3 }}
          />

          {/* Select Unit */}
          <Typography
            variant="h6"
            sx={{ mb: 1, fontWeight: 400, fontSize: "1.1rem" }}
          >
            Select Unit
          </Typography>
          <Autocomplete
            disabled={unitSelectDisabled || onlyView}
            fullWidth
            options={unitOptions}
            value={selectedUnitValue}
            onChange={handleSelectUnit}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select Unit"
                variant="outlined"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingUnits ? (
                        <CircularProgress color="inherit" size={16} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{ mb: 3 }}
          />

          {/* Access Group */}
          <Typography
            variant="h6"
            sx={{ mb: 1, fontWeight: 400, fontSize: "1.1rem" }}
          >
            Access Group
          </Typography>
          <Autocomplete
            fullWidth
            multiple
            options={accessGroupsOptions}
            value={accessGroupIdsValue}
            onChange={handleSelectAccessGroup}
            disabled={identityLocked}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Access Group"
                variant="outlined"
                size="small"
              />
            )}
            sx={{ mb: 3 }}
          />

          {/* Lease Dates */}
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <Typography
                variant="h6"
                sx={{ mb: 1, fontWeight: 400, fontSize: "1.1rem" }}
              >
                Start Date
              </Typography>
              <DatePicker
                disabled={identityLocked}
                sx={{ width: "100%" }}
                value={isEdit || onlyView ? dayjs(formData.leaseStart) : null}
                slotProps={{
                  textField: {
                    size: "small",
                    variant: "outlined",
                    placeholder: "Start Date",
                  },
                }}
                onAccept={(newValue: any) => {
                  setFormData({
                    ...formData,
                    leaseStart: dayjs(newValue.$d).toISOString(),
                  });
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography
                variant="h6"
                sx={{ mb: 1, fontWeight: 400, fontSize: "1.1rem" }}
              >
                End Date
              </Typography>
              <DatePicker
                disabled={identityLocked}
                sx={{ width: "100%" }}
                value={isEdit || onlyView ? dayjs(formData.leaseEnd) : null}
                slotProps={{
                  textField: {
                    size: "small",
                    variant: "outlined",
                    placeholder: "End Date",
                  },
                }}
                onAccept={(newValue: any) => {
                  setFormData({
                    ...formData,
                    leaseEnd: dayjs(newValue.$d).toISOString(),
                  });
                }}
              />
            </Grid>
          </Grid>
        </Box>
      }
    />
  );
}
